/**
 * Global socket provider — establishes one STOMP session per authenticated user.
 */
import { useEffect, useRef } from "react";
import { useRecoilCallback } from "recoil";
import {
  CHAT_SOCKET_STATUS,
  chatCurrentUserIdState,
  chatSocketConnectedState,
} from "../experience/chat/state/chatState";
import socketService from "./socketService";
import { SOCKET_TOPICS } from "./socketConfig";
import {
  bootstrapChatOnSocketConnect,
  createRefreshChatRooms,
  handleUserTopicEvent,
  resetChatSocketRecoilState,
} from "./socketEventHandlers";
import { getSocketAuthToken } from "./socketAuth";
import {
  isSessionActive,
  useAuthenticatedSession,
} from "./useAuthenticatedSession";

const TOKEN_RETRY_MS = 500;
const MAX_TOKEN_RETRIES = 10;

const logSocketStatus = (status) => {
  const label =
    status === CHAT_SOCKET_STATUS.CONNECTED
      ? "connected"
      : status === CHAT_SOCKET_STATUS.CONNECTING
        ? "connecting"
        : "disconnected";
  console.log(`[Chat Socket] ${label}`);
};

const SocketProvider = ({ children }) => {
  const user = useAuthenticatedSession();
  const userTopicCleanupRef = useRef(null);
  const tokenRetryTimeoutRef = useRef(null);
  const tokenRetryCountRef = useRef(0);
  const connectAttemptRef = useRef(0);

  const userIdentifier = user?.userIdentifier || null;
  const organizationIdentifier = user?.organizationIdentifier || null;
  const sessionActive = isSessionActive(user);

  const handleUserEvent = useRecoilCallback(
    ({ snapshot, set }) =>
      (rawBody) => {
        const refreshRooms = createRefreshChatRooms(
          organizationIdentifier,
          userIdentifier,
        );

        handleUserTopicEvent(rawBody, {
          get: (atom) => snapshot.getLoadable(atom).contents,
          set,
          refreshRooms: () => refreshRooms(set),
          userIdentifier,
        });
      },
    [organizationIdentifier, userIdentifier],
  );

  const handleUserEventRef = useRef(handleUserEvent);
  handleUserEventRef.current = handleUserEvent;

  const disconnectSocket = useRecoilCallback(
    ({ set }) =>
      () => {
        if (tokenRetryTimeoutRef.current) {
          clearTimeout(tokenRetryTimeoutRef.current);
          tokenRetryTimeoutRef.current = null;
        }
        tokenRetryCountRef.current = 0;
        userTopicCleanupRef.current?.();
        userTopicCleanupRef.current = null;
        socketService.disconnect();
        resetChatSocketRecoilState(set);
        set(chatSocketConnectedState, CHAT_SOCKET_STATUS.DISCONNECTED);
        logSocketStatus(CHAT_SOCKET_STATUS.DISCONNECTED);
      },
    [],
  );

  const bootstrapChatState = useRecoilCallback(
    ({ snapshot, set }) =>
      async () => {
        await bootstrapChatOnSocketConnect(
          organizationIdentifier,
          userIdentifier,
          set,
          (atom) => snapshot.getLoadable(atom).contents,
        );
      },
    [organizationIdentifier, userIdentifier],
  );

  const bootstrapChatStateRef = useRef(bootstrapChatState);
  bootstrapChatStateRef.current = bootstrapChatState;

  const connectSocket = useRecoilCallback(
    ({ set }) =>
      () => {
        if (!sessionActive) {
          return;
        }

        const token = getSocketAuthToken();
        if (!token) {
          if (tokenRetryCountRef.current < MAX_TOKEN_RETRIES) {
            tokenRetryCountRef.current += 1;
            set(chatSocketConnectedState, CHAT_SOCKET_STATUS.CONNECTING);
            logSocketStatus(CHAT_SOCKET_STATUS.CONNECTING);
            tokenRetryTimeoutRef.current = setTimeout(() => {
              connectSocket();
            }, TOKEN_RETRY_MS);
          } else {
            set(chatSocketConnectedState, CHAT_SOCKET_STATUS.DISCONNECTED);
            logSocketStatus(CHAT_SOCKET_STATUS.DISCONNECTED);
          }
          return;
        }

        tokenRetryCountRef.current = 0;
        connectAttemptRef.current += 1;
        const attemptId = connectAttemptRef.current;

        set(chatCurrentUserIdState, userIdentifier);
        set(chatSocketConnectedState, CHAT_SOCKET_STATUS.CONNECTING);
        logSocketStatus(CHAT_SOCKET_STATUS.CONNECTING);

        socketService.connect({
          onStatusChange: (status) => {
            if (attemptId !== connectAttemptRef.current) {
              return;
            }

            const mappedStatus =
              status === "connected"
                ? CHAT_SOCKET_STATUS.CONNECTED
                : status === "connecting"
                  ? CHAT_SOCKET_STATUS.CONNECTING
                  : CHAT_SOCKET_STATUS.DISCONNECTED;
            set(chatSocketConnectedState, mappedStatus);
            logSocketStatus(mappedStatus);
          },
          onConnected: () => {
            if (attemptId !== connectAttemptRef.current) {
              return;
            }

            userTopicCleanupRef.current?.();
            userTopicCleanupRef.current = socketService.subscribe(
              SOCKET_TOPICS.user(userIdentifier),
              (rawBody) => handleUserEventRef.current(rawBody),
            );
            bootstrapChatStateRef.current();
          },
        });
      },
    [organizationIdentifier, sessionActive, userIdentifier],
  );

  useEffect(() => {
    if (sessionActive) {
      connectSocket();
      return () => {
        connectAttemptRef.current += 1;
        disconnectSocket();
      };
    }

    disconnectSocket();
    return undefined;
  }, [
    connectSocket,
    disconnectSocket,
    sessionActive,
    userIdentifier,
    organizationIdentifier,
  ]);

  return children;
};

export default SocketProvider;
